import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import { getIsRecruitingOrTrainingProject } from "@repo/app-types/helpers/utilities";
import { FullPageSkeleton } from "@repo/ui/Skeletons/skeletons";
import { type FC, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { selectNonEmptyInterviewsWithContactsAndActivitiesForProject } from "../interviews/interviewsSlice.js";
import {
  clearInsightsParameters,
  fetchInsightsAsync,
  selectInsightsLoadingState,
} from "./insightsSlice.js";
import { InsightsTab } from "./insightsTab.js";
import { RecruitingInsightsTab } from "./recruitingInsightsTab.js";

interface InsightsTabContainerProps {
  project: ProjectWithInterviewCount;
}

// This component is responsible for loading insights data based on the current parameters
export const InsightsTabContainer: FC<InsightsTabContainerProps> = ({
  project,
}) => {
  // Enter this branch for all non-survey projects. In practice, we should only hit this branch for recruiting projects, since training projects have their own component hierarchy entirely.
  if (getIsRecruitingOrTrainingProject(project)) {
    return <RecruitingInsightsTab projectId={project.id} />;
  }

  const dispatch = useAppDispatch();
  const insightsLoadingState = useAppSelector(selectInsightsLoadingState);

  const interviews = useAppSelector((state) =>
    selectNonEmptyInterviewsWithContactsAndActivitiesForProject(
      state,
      project.id,
    ),
  );

  // Load insights on initial render
  useEffect(() => {
    dispatch(
      fetchInsightsAsync({
        projectId: project.id,
      }),
    );

    // Reset insights state on unmount
    return () => {
      dispatch(clearInsightsParameters(undefined));
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-6xl py-4 text-lg text-gray-600">
      {insightsLoadingState === "succeeded" ? (
        <InsightsTab project={project} interviews={interviews} />
      ) : (
        <FullPageSkeleton />
      )}
    </div>
  );
};
